/**
 * Data layer for the COMPANY panel's Scans section.
 *
 * Three camera-based self-checks — Skin, Teeth and Age — reported as one
 * behaviour rather than three features, because that is what they are: the
 * headline number is how many people photograph themselves for a health check
 * at all, not how many skin scans got taken.
 *
 * NOTHING ANY SCAN FOUND REACHES THIS LAYER. No skin score, risk level or
 * illness match; no hygiene score, band, summary or per-tooth verdict; no
 * facial age, real age or wellbeing reading; no image and no PDF link. Those
 * results went to the person who took the scan, and the endpoints behind this
 * file never project them — see Backend services/scanHubService.js for the
 * full reasoning.
 *
 * What comes back is what somebody DID and how the software behaved: a scan
 * was taken, on this day, with this many of the requested photos, the analysis
 * finished or failed, it took this long, and they came back (or didn't).
 *
 * ONE EXCEPTION, on the Teeth page only: two self-reported questionnaire
 * answers — time since the last dentist visit, and brushing frequency. Those
 * are behaviours on a fixed scale that somebody typed about themselves, not an
 * AI's reading of a photograph, and they are the finding a company acts on by
 * buying a dental plan. They arrive aggregate-only and are suppressed by the
 * backend below a minimum cohort, so a small department can't be read back to
 * individuals. Gum bleeding, sensitivity and smoking are not reported at all.
 *
 * Backed by the company/v1 scan-hub endpoints (token-scoped to the company; the
 * B2B/Members toggle is appended by api/companyApi.js). Every fetcher takes an
 * optional `department` so the section's filter can narrow the whole page.
 */

import {
  getCompanyScanDashboard,
  getCompanyScan,
  getCompanyScanUsers,
  getCompanyScanUserDetail
} from '../../../../api/companyApi';
import { unwrap } from '../../../SleepHub/sleepHubApi';

/** Section overview — cross-scan first, then a row per scan. */
export const adaptOverview = (data) => ({
  totalUsers: data.totalUsers ?? 0,
  activeUsers: data.activeUsers ?? 0,
  adoption: data.adoption ?? 0,
  totalScans: data.totalScans ?? 0,
  avgScansPerActiveUser: data.avgScansPerActiveUser ?? 0,

  scanBreakdown: data.scanBreakdown || [],

  // Breadth: one scan type is a try, all three is somebody using the feature.
  typesPerUser: data.typesPerUser || [],
  multiScanUsers: data.multiScanUsers ?? 0,
  volumeSplit: data.volumeSplit || [],

  // Whether the AI pipeline actually delivered. This is a verdict on the
  // software, and it is the only verdict this section carries.
  reliability: data.reliability || {},
  statusSplit: data.statusSplit || [],

  // Backend zero-fills the last 30 days; charts consume plain series.
  scansTrend: (data.dailyTrend || []).map((day) => day.scans || 0),
  peopleTrend: (data.dailyTrend || []).map((day) => day.users || 0)
});

/**
 * One scan's page. The common block is identical for all three so the pages
 * read the same; `detail` is passed through untouched and the page decides what
 * to render for its own scan.
 */
export const adaptScan = (data) => ({
  ...data,
  scan: data.scan || null,
  totalUsers: data.totalUsers ?? 0,
  scans: data.scans ?? 0,
  users: data.users ?? 0,
  adoption: data.adoption ?? 0,
  avgPerUser: data.avgPerUser ?? 0,
  firstDate: data.firstDate || null,
  lastDate: data.lastDate || null,

  reliability: data.reliability || {},
  statusSplit: data.statusSplit || [],
  resultTimeSplit: data.resultTimeSplit || [],
  funnel: data.funnel || [],

  repeatUsers: data.repeatUsers ?? 0,
  repeatRate: data.repeatRate ?? 0,
  volumeSplit: data.volumeSplit || [],
  cadenceSplit: data.cadenceSplit || [],

  weekday: data.weekday || [],
  detail: data.detail || {},
  scansTrend: (data.dailyTrend || []).map((day) => day.scans || 0),
  peopleTrend: (data.dailyTrend || []).map((day) => day.users || 0)
});

export const fetchCompanyScanOverview = async (department = '') => {
  try {
    return adaptOverview(unwrap(await getCompanyScanDashboard(department)));
  } catch (error) {
    console.error('fetchCompanyScanOverview error:', error);
    return null;
  }
};

export const fetchCompanyScan = async (scan, department = '') => {
  try {
    return adaptScan(unwrap(await getCompanyScan(scan, department)));
  } catch (error) {
    console.error('fetchCompanyScan error:', error);
    return null;
  }
};

// Matches the ScanUsersTable fetcher contract.
export const fetchCompanyScanUsers = async ({
  page = 1,
  perPage = 10,
  search = '',
  department = ''
} = {}) => {
  try {
    const data = unwrap(await getCompanyScanUsers(page, perPage, search, department));
    return { data: data.data || [], totalCount: data.totalCount || 0 };
  } catch (error) {
    console.error('fetchCompanyScanUsers error:', error);
    return { data: [], totalCount: 0 };
  }
};

/**
 * One employee's/member's scan history ({ user, scans, scanMix, history,
 * dailyTrend }). Counts, dates and whether the software finished — every
 * history row says when a scan happened and whether it worked, and nothing at
 * all about what it found. The backend 404s any user outside this company, so
 * no scoping is needed here.
 */
export const fetchCompanyScanUserDetail = async (userId) => {
  try {
    return unwrap(await getCompanyScanUserDetail(userId));
  } catch (error) {
    console.error('fetchCompanyScanUserDetail error:', error);
    return null;
  }
};
